import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getBrowserTimezone, isValidTimezone } from 'utils/timezoneUtils';

/**
 * TimezoneSelector Component
 *
 * Searchable timezone dropdown with full IANA timezone support.
 * Auto-detects browser timezone as default.
 *
 * @param {Object} props
 * @param {string} props.value - Currently selected timezone
 * @param {function} props.onChange - Callback when timezone changes
 * @param {string} props.label - Label text (e.g. "老師時區")
 * @param {boolean} props.compact - Use compact styling for embedding in modals
 *
 * Legacy props (backward compatible):
 * @param {string} props.userTimezone - User's saved timezone from profile
 * @param {function} props.onTimezoneChange - Callback when timezone changes
 */

// Common timezones shown at the top of the list
const COMMON_TIMEZONES = [
  'Asia/Taipei', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Shanghai',
  'Asia/Hong_Kong', 'Asia/Singapore', 'Asia/Bangkok',
  'America/New_York', 'America/Los_Angeles', 'America/Chicago',
  'Europe/London', 'Europe/Paris', 'Australia/Sydney', 'UTC'
];

// Get UTC offset string for a timezone
function getUtcOffset(tz) {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find(p => p.type === 'timeZoneName');
    return offsetPart?.value || '';
  } catch {
    return '';
  }
}

// Format timezone for display: "Asia/Taipei (UTC+8)"
function formatTimezone(tz) {
  const offset = getUtcOffset(tz);
  const displayName = tz.replace(/_/g, ' ').replace(/\//g, ' / ');
  return `${displayName} (${offset})`;
}

// Get all IANA timezones
function getAllTimezones() {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    // Fallback for older browsers
    return COMMON_TIMEZONES;
  }
}

const TimezoneSelector = ({
  value,
  onChange,
  label,
  compact = false,
  // Legacy props
  userTimezone = null,
  onTimezoneChange,
}) => {
  // Support both new and legacy API
  const effectiveValue = value || userTimezone;
  const effectiveOnChange = onChange || onTimezoneChange;

  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  // Build timezone list: common first, then all others
  const allTimezones = useMemo(() => {
    const all = getAllTimezones();
    const commonSet = new Set(COMMON_TIMEZONES);
    const others = all.filter(tz => !commonSet.has(tz));
    return { common: COMMON_TIMEZONES, others };
  }, []);

  // Filter by search
  const filtered = useMemo(() => {
    if (!search.trim()) return allTimezones;
    const q = search.toLowerCase();
    return {
      common: allTimezones.common.filter(tz =>
        tz.toLowerCase().includes(q) || formatTimezone(tz).toLowerCase().includes(q)
      ),
      others: allTimezones.others.filter(tz =>
        tz.toLowerCase().includes(q) || formatTimezone(tz).toLowerCase().includes(q)
      )
    };
  }, [search, allTimezones]);

  // Initialize
  useEffect(() => {
    const initial = getInitialTimezone();
    setSelectedTimezone(initial);
    if (effectiveOnChange) effectiveOnChange(initial);
  }, []);

  // Sync from parent
  useEffect(() => {
    if (effectiveValue && effectiveValue !== selectedTimezone) {
      setSelectedTimezone(effectiveValue);
    }
  }, [effectiveValue]);

  function getInitialTimezone() {
    if (effectiveValue && effectiveValue !== 'UTC' && isValidTimezone(effectiveValue)) {
      return effectiveValue;
    }
    const browserTz = getBrowserTimezone();
    if (browserTz && isValidTimezone(browserTz)) return browserTz;
    return 'Asia/Taipei';
  }

  const handleSelect = (tz) => {
    setSelectedTimezone(tz);
    if (effectiveOnChange) effectiveOnChange(tz);
    setIsOpen(false);
    setSearch('');
  };

  // Focus search on open
  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const py = compact ? 'py-1.5' : 'py-3';
  const textSize = compact ? 'text-xs' : 'text-sm';

  const renderItem = (tz) => (
    <button
      key={tz}
      type="button"
      onClick={() => handleSelect(tz)}
      className={`w-full text-left px-3 ${py} ${textSize} hover:bg-blue-50 transition-colors cursor-pointer ${
        selectedTimezone === tz ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-700'
      }`}
    >
      {formatTimezone(tz)}
    </button>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className={`block ${compact ? 'text-xs' : 'text-sm'} text-gray-500 mb-1`}>{label}</label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 ${py} border border-gray-300 rounded-lg ${textSize} text-left bg-white hover:bg-gray-50 flex items-center justify-between transition-colors cursor-pointer`}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <svg className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-gray-400 flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-gray-900 truncate">{formatTimezone(selectedTimezone)}</span>
        </div>
        <svg
          className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-[280px] overflow-hidden flex flex-col">
          {/* Search */}
          <div className="p-2 border-b border-gray-100">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search timezone..."
              className={`w-full px-3 py-1.5 ${textSize} border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400`}
            />
          </div>
          {/* List */}
          <div className="overflow-y-auto overscroll-contain">
            {filtered.common.length > 0 && (
              <>
                {!search.trim() && (
                  <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Common</div>
                )}
                {filtered.common.map(renderItem)}
              </>
            )}
            {filtered.others.length > 0 && (
              <>
                {!search.trim() && filtered.common.length > 0 && (
                  <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-t border-gray-100 mt-1 pt-1">All</div>
                )}
                {filtered.others.map(renderItem)}
              </>
            )}
            {filtered.common.length === 0 && filtered.others.length === 0 && (
              <div className={`px-3 py-4 ${textSize} text-gray-400 text-center`}>No timezones found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimezoneSelector;
